// Banner detail normalizers.

import { BANNER_ALIAS_DOMAIN_ROW_ERROR } from 'src/services/consts/banner/create.const'
import {
  getDefaultCatchAllOwner,
  mapVisibleCountriesToVisibilityForm,
} from 'src/services/helpers/banner/behaviour.helper'
import { isValidDomain, normalizeDomainHost } from 'src/services/helpers/security.helper'

/**
 * @typedef {Object} CreateBannerPayload
 * @property {string|null} [url]
 * @property {string|null} [siteName]
 * @property {string|null} [description]
 * @property {string|null} [privacyPolicyUrl]
 * @property {string|null} [defaultLocale]
 * @property {string[]} [availableLocales]
 * @property {string[]} [regulations]
 * @property {string[]} [aliasDomains]
 * @property {boolean} [googleConsentMode]
 * @property {string} [catchAllOwner] - UI shape; mapped to `visibleCountries` in banner.service
 * (accountId is injected by the httpService request interceptor — never set here)
 */

/**
 * @typedef {Object} UpdateBannerPayload
 * @property {string} [url]
 * @property {string} [siteName]
 * @property {string} [description]
 * @property {string} [privacyPolicyUrl]
 * @property {string} [defaultLocale]
 * @property {string[]} [availableLocales]
 * @property {string[]} [regulations]
 * @property {string[]} [aliasDomains]
 * @property {boolean} [googleConsentMode]
 * @property {string} [catchAllOwner] - UI shape; mapped to `visibleCountries` in banner.service
 * @property {Object} [appearance] - partial or full BannerAppearance shape
 */

/**
 * @typedef {Object} BannerAppearanceActions
 * @property {boolean} managePreferences
 */

/**
 * @typedef {Object} BannerAppearance
 * @property {string|null} accentColor
 * @property {boolean} branding
 * @property {BannerAppearanceActions|null} actions
 * @property {string|null} theme - 'light' | 'dark' | 'glass'
 * @property {string|null} position - 'bottom' | 'top' | 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
 * @property {string|null} type - 'bar' | 'popup'
 * @property {boolean} blocking
 */

/** @returns {BannerAppearanceActions|null} */
function normalizeBannerAppearanceActions(raw) {
  if (!raw || typeof raw !== 'object') return null
  return {
    managePreferences: raw.managePreferences ?? false,
  }
}

/** @returns {BannerAppearance|null} */
function normalizeBannerAppearance(raw) {
  if (!raw || typeof raw !== 'object') return null
  return {
    accentColor: raw.accentColor ? String(raw.accentColor).toLowerCase() : null,
    branding: raw.branding ?? false,
    actions: normalizeBannerAppearanceActions(raw.actions),
    theme: raw.theme ?? null,
    position: raw.position ?? null,
    type: raw.type ?? null,
    blocking: raw.blocking ?? false,
  }
}

/**
 * @typedef {Object} BannerDetail
 * @property {string} siteId
 * @property {string|null} url
 * @property {string|null} siteName
 * @property {string|null} description
 * @property {string|null} privacyPolicyUrl
 * @property {string|null} defaultLocale
 * @property {string[]|null} availableLocales - API list as stored; `[]` means every language, `null` means the field was absent
 * @property {string[]} regulations
 * @property {string[]} aliasDomains
 * @property {string} catchAllOwner - resolved once here from the wire `visibleCountries` map;
 * `CATCH_ALL_OWNER.NONE` or a `BANNER_REGULATION` value — see `behaviour.helper.js`
 * @property {boolean} googleConsentMode
 * @property {BannerAppearance|null} appearance
 * @property {boolean} active
 * @property {string|null} createdAt
 * @property {string|null} lastScanned
 * @property {number|null} totalItems
 * @property {number|null} unclassifiedItems
 * @property {string|null} deletedAt
 * @property {string|null} accountId
 */

/**
 * Parses the inbound `visibleCountries` regulation map. Any other shape —
 * including a bare array — is treated as no data; `normalizeBannerDetail`
 * falls back to `getDefaultCatchAllOwner` in that case. Not exposed on
 * `BannerDetail` itself — `regulations` + `catchAllOwner` fully capture what
 * this map conveys under the sentinel-only model, so callers never need the
 * raw map back.
 * @param {Object} raw
 * @returns {Record<string, string[]>|null}
 */
function normalizeVisibleCountries(raw) {
  const value = raw?.visibleCountries
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null

  /** @type {Record<string, string[]>} */
  const map = {}
  for (const [key, countries] of Object.entries(value)) {
    map[key] = Array.isArray(countries) ? [...countries] : []
  }
  return map
}

/**
 * Shape alias-domains for create/update write path.
 * @param {string[]|null|undefined|*} rows
 * @returns {string[]}
 */
export function normalizeAliasDomainsForPayload(rows) {
  if (!Array.isArray(rows)) return []
  return rows
    .map((r) => (r ?? '').trim())
    .filter(Boolean)
    .map(normalizeDomainHost)
    .filter(Boolean)
}

/**
 * Order-sensitive equality on normalized alias-domain arrays.
 * @param {string[]|null|undefined} a
 * @param {string[]|null|undefined} b
 * @returns {boolean}
 */
export function areBannerAliasDomainsEqual(a, b) {
  const na = normalizeAliasDomainsForPayload(a)
  const nb = normalizeAliasDomainsForPayload(b)
  return na.length === nb.length && na.every((v, i) => v === nb[i])
}

/**
 * Returns a reason key when a committed alias-domain row is invalid, otherwise null.
 * Empty/whitespace rows are not invalid.
 * @param {{ domain?: string|null, index: number, aliasDomains?: string[], mainUrl?: string|null }} params
 * @returns {string|null}
 */
export function getBannerAliasDomainRowError({ domain, index, aliasDomains = [], mainUrl }) {
  const trimmed = domain?.trim()
  if (!trimmed) return null

  if (!isValidDomain(trimmed)) {
    return BANNER_ALIAS_DOMAIN_ROW_ERROR.INVALID_FORMAT
  }

  const normalized = normalizeDomainHost(trimmed)
  if (!normalized) {
    return BANNER_ALIAS_DOMAIN_ROW_ERROR.INVALID_FORMAT
  }

  const normalizedMain = mainUrl?.trim() ? normalizeDomainHost(mainUrl.trim()) : null
  if (normalizedMain && normalized === normalizedMain) {
    return BANNER_ALIAS_DOMAIN_ROW_ERROR.MATCHES_MAIN_URL
  }

  for (let i = 0; i < aliasDomains.length; i++) {
    if (i === index) continue
    const sibling = aliasDomains[i]?.trim()
    if (!sibling || !isValidDomain(sibling)) continue
    const normalizedSibling = normalizeDomainHost(sibling)
    if (normalizedSibling && normalizedSibling === normalized) {
      return BANNER_ALIAS_DOMAIN_ROW_ERROR.DUPLICATE
    }
  }

  return null
}

/**
 * @param {{ domain?: string|null, index: number, aliasDomains?: string[], mainUrl?: string|null }} params
 * @returns {boolean}
 */
export function isBannerAliasDomainRowInvalid(params) {
  return !!getBannerAliasDomainRowError(params)
}

/** @returns {BannerDetail|null} */
export function normalizeBannerDetail(raw) {
  if (!raw || typeof raw !== 'object' || !raw.siteId) return null
  const regulations = Array.isArray(raw.regulations) ? raw.regulations : []
  const visibleCountries = normalizeVisibleCountries(raw)
  const catchAllOwner = visibleCountries
    ? mapVisibleCountriesToVisibilityForm(visibleCountries, regulations).catchAllOwner
    : getDefaultCatchAllOwner(regulations)

  return {
    siteId: raw.siteId,
    url: raw.url ?? null,
    siteName: raw.siteName ?? null,
    description: raw.description ?? null,
    privacyPolicyUrl: raw.privacyPolicyUrl ?? null,
    defaultLocale: raw.defaultLocale ?? null,
    availableLocales: Array.isArray(raw.availableLocales) ? [...raw.availableLocales] : null,
    regulations,
    aliasDomains: Array.isArray(raw.aliasDomains) ? raw.aliasDomains : [],
    catchAllOwner,
    googleConsentMode: raw.googleConsentMode ?? false,
    appearance: normalizeBannerAppearance(raw.appearance),
    active: raw.active ?? false,
    createdAt: raw.createdAt ?? null,
    lastScanned: raw.lastScanned ?? null,
    deletedAt: raw.deletedAt ?? null,
    totalItems: raw.totalItems ?? null,
    unclassifiedItems: raw.unclassifiedItems ?? null,
    accountId: raw.accountId ?? null,
  }
}
